import React, { useEffect, useMemo, useState } from 'react';

const IMAGER_URL = 'https://img.franklyorigins.net/habbo-imaging/avatarimage';

const CUSTOM_MAP = {
    frank_hat: 'ha-3001-1',
};

export function getHabboImageUrl({
                                     habbo = null,
                                     hotel = 'COM',
                                     figure = null,
                                     action = 'std',
                                     direction = 4,
                                     head_direction = 4,
                                     size = 'b',
                                     gesture = 'std',
                                     frame = 0,
                                     crr = null,
                                     shadow = true,
                                     head = 0,
                                     headonly = false,
                                     custom = null,
                                 }) {
    const params = new URLSearchParams();

    if (figure) {
        params.append('figure', figure);
    } else if (habbo && hotel) {
        params.append('habbo', habbo);
        params.append('hotel', hotel.toUpperCase());
    }

    if (action !== 'std') params.append('action', action);
    if (direction !== 2) params.append('direction', direction);
    if (head_direction !== 2) params.append('head_direction', head_direction);
    if (size !== 'b') params.append('size', size);
    if (gesture !== 'std') params.append('gesture', gesture);
    if (frame !== 0) params.append('frame', frame);
    if (crr !== null) params.append('crr', crr);
    if (shadow === false) params.append('shadow', 'false');
    if (head !== 0) params.append('head', head);
    if (headonly) params.append('headonly', 'true');

    if (custom && CUSTOM_MAP[custom]) {
        params.append('custom', CUSTOM_MAP[custom]);
    }

    return `${IMAGER_URL}?${params.toString()}`;
}

/*
================================================================================
TEMP PATCH
================================================================================
*/
const PATCH_FIG_TTL_MS = 15 * 60 * 1000;
const FIG_CACHE_KEY = 'habbo_fig_cache_v1';
const ORIGINS_CONCURRENCY = 6;

const HOTEL_API_BASE = {
    COM: 'https://origins.habbo.com',
    BR: 'https://origins.habbo.com.br',
    ES: 'https://origins.habbo.es',
};

const readFigCache = () => {
    try {
        const raw = sessionStorage.getItem(FIG_CACHE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const writeFigCache = (cache) => {
    try {
        sessionStorage.setItem(FIG_CACHE_KEY, JSON.stringify(cache));
    } catch {}
};

const getKeyLower = (hotel, username) => {
    const h = (hotel || 'COM').toUpperCase();
    const u = (username || '').trim();
    if (!u) return null;
    return `${h}:${u}`.toLowerCase();
};

const getCachedFigureWithinTTL = (keyLower) => {
    if (!keyLower) return null;

    const cache = readFigCache();
    const entry = cache[keyLower];
    if (!entry?.figureString || !entry?.t) return null;

    if (Date.now() - entry.t > PATCH_FIG_TTL_MS) {
        try {
            const next = readFigCache();
            delete next[keyLower];
            writeFigCache(next);
        } catch {}
        return null;
    }

    return entry.figureString;
};

const setCachedFigure = (keyLower, figureString) => {
    if (!keyLower || !figureString) return;
    const next = readFigCache();
    next[keyLower] = { figureString, t: Date.now() };
    writeFigCache(next);
};

const queue = [];
let active = 0;

const pump = () => {
    while (active < ORIGINS_CONCURRENCY && queue.length) {
        const job = queue.shift();
        active += 1;
        Promise.resolve()
            .then(job.fn)
            .then(job.resolve, job.reject)
            .finally(() => {
                active -= 1;
                pump();
            });
    }
};

const runLimited = (fn) =>
    new Promise((resolve, reject) => {
        queue.push({ fn, resolve, reject });
        pump();
    });

const inFlightLookups = new Map();

async function resolveFigureString({ username, hotel, signal }) {
    const name = (username || '').trim();
    const h = (hotel || 'COM').toUpperCase();
    if (!name) throw new Error('Missing username');

    const key = getKeyLower(h, name);

    const cached = getCachedFigureWithinTTL(key);
    if (cached) return cached;

    if (inFlightLookups.has(key)) return inFlightLookups.get(key);

    const p = runLimited(async () => {
        const base = HOTEL_API_BASE[h] || HOTEL_API_BASE.COM;
        const url = `${base}/api/public/users?name=${encodeURIComponent(name)}`;

        const res = await fetch(url, { signal, headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const fig = data?.figureString;
        if (!fig) throw new Error('No figureString in response');

        setCachedFigure(key, fig);
        return fig;
    });

    inFlightLookups.set(key, p);
    try {
        return await p;
    } finally {
        inFlightLookups.delete(key);
    }
}
/* ============================== END TEMP PATCH ============================== */

function HabboImage({
                        habbo = null,
                        hotel = 'COM',
                        figure = null,
                        action = 'std',
                        direction = 4,
                        head_direction = 4,
                        size = 'b',
                        gesture = 'std',
                        frame = 0,
                        crr = null,
                        shadow = true,
                        head = 0,
                        headonly = false,
                        custom = null,
                        className = '',
                        style = {},
                        ...props
                    }) {
    const [resolvedFigure, setResolvedFigure] = useState(null);
    const [shouldFallbackToHabbo, setShouldFallbackToHabbo] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

        setResolvedFigure(null);
        setShouldFallbackToHabbo(false);

        if (figure) return;
        if (!habbo || !hotel) return;

        const key = getKeyLower(hotel, habbo);
        const cached = getCachedFigureWithinTTL(key);
        if (cached) {
            setResolvedFigure(cached);
            return;
        }

        (async () => {
            try {
                const fig = await resolveFigureString({
                    username: habbo,
                    hotel,
                    signal: controller?.signal,
                });
                if (cancelled) return;
                setResolvedFigure(fig);
            } catch {
                if (cancelled) return;
                setShouldFallbackToHabbo(true);
            }
        })();

        return () => {
            cancelled = true;
            try {
                controller?.abort();
            } catch {}
        };
    }, [habbo, hotel, figure]);

    const imageUrl = useMemo(() => {
        const effectiveFigure = figure || resolvedFigure;
        const allowHabboFallback = !effectiveFigure && shouldFallbackToHabbo;

        return getHabboImageUrl({
            habbo: allowHabboFallback ? habbo : null,
            hotel: allowHabboFallback ? hotel : 'COM',
            figure: effectiveFigure,
            action,
            direction,
            head_direction,
            size,
            gesture,
            frame,
            crr,
            shadow,
            head,
            headonly,
            custom,
        });
    }, [
        habbo,
        hotel,
        figure,
        resolvedFigure,
        shouldFallbackToHabbo,
        action,
        direction,
        head_direction,
        size,
        gesture,
        frame,
        crr,
        shadow,
        head,
        headonly,
        custom,
    ]);

    return <img src={imageUrl} className={className} style={{ imageRendering: "pixelated", ...style }} {...props} />;
}

export default HabboImage;